import { ANALYTICS_EVENTS } from '@syncanix/shared';
import { Logo } from '@syncanix/ui';
import { useLocation } from 'react-router-dom';

import { track } from '@/analytics/track';
import { CtaLink, ctaClasses } from '@/components/bedrock';
import { CHROME_CTAS, CHROME_ROUTES, navLinksFor } from '@/components/chrome';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { useT } from '@/core/I18nProvider';
import { appLoginUrl } from '@/lib/site-url';

import { LanguageSwitcher } from './LanguageSwitcher';
import { isNavLinkActive } from './nav-active';
import { NavMenu } from './NavMenu';

import type { ReactElement } from 'react';

/**
 * Site-wide marketing header — ONE row, at every width.
 *
 * ## The two-storey bar this replaces
 *
 * Below 1280px the header used to render a second row: a horizontally
 * scrolling strip of section links under the main bar. The reasoning was
 * sound in isolation (no hamburger, every link one tap away) and the result
 * was not — a header that changed height at a breakpoint, a strip that
 * clipped its last label mid-word, and no affordance anywhere saying it could
 * be scrolled. `--bd-nav-h` claims the bar is 64px; on a laptop it was 106.
 *
 * The bar is now a fixed 64px everywhere and the links that do not fit go into
 * `NavMenu` — see that file for why a disclosure beats the strip, and why it is
 * a disclosure rather than an APG menu widget.
 *
 * ## What the bar carries at each width
 *
 * | width  | brand            | nav         | switcher | sign in | talk to sales |
 * | ------ | ---------------- | ----------- | -------- | ------- | ------------- |
 * | ≥ lg   | mark + wordmark  | six links   | in bar   | in bar  | in bar        |
 * | sm–lg  | mark + wordmark  | menu button | in bar   | in menu | in bar        |
 * | < sm   | mark             | menu button | icon     | in menu | in bar        |
 *
 * The demotions are ordered by what a first-time visitor loses least by not
 * seeing: a second wordmark the mark already carries, then the returning-user
 * sign-in. The primary CTA never leaves the bar, because it is the one thing
 * the page is asking for; nor does the language switcher, which shrinks to its
 * globe rather than moving — a language control an Arabic or Hebrew reader has
 * to open a menu to find is a real cost, where a duplicated wordmark is not.
 *
 * Measured at 390px in the widest locale (German, "Vertrieb kontaktieren"):
 * 32 padding + 30 mark + 31 switcher + 173 CTA + 40 menu + gaps = ~330 of 390.
 *
 * `lg` (1024px) rather than `md`: the six labels plus the switcher, sign-in and
 * CTA measure ~830px in English and ~930px in German, so `md` (768px) would put
 * the row into a width it cannot hold — the failure `ui-design.md` § 7 names,
 * and the one `scripts/a11y/nav-fit.mjs` measures across locale × width.
 *
 * ## "log in" is an app link, not a router link
 *
 * It points at the *app* origin (`app.<domain>`), a different host from this
 * static marketing site, so it is a plain `<a>` composed from the `site-url`
 * helpers (AF-426 single-source guard) rather than a react-router `<Link>`.
 * "talk to sales" is an in-SPA locale-aware `<Link>` to `/contact`.
 *
 * ## Lowercase CTA labels (Marketer hat, locked)
 *
 * "log in" / "talk to sales" follow the brand-voice convention the hero and the
 * SkipLink use. The "syncanix" wordmark is lowercase for a different reason: the
 * mark beside it carries the gradient, and a capitalised wordmark competes with
 * it for weight.
 *
 * The `<SkipLink />` renders before this header (and above it at `z-50` against
 * `z-40`) so keyboard users can jump straight past the nav to `#main`.
 */
const SECTION_LINKS = navLinksFor('site');

export function Header(): ReactElement {
  const { t } = useT();
  const { pathname } = useLocation();

  return (
    <header
      role="banner"
      aria-label={t('header.siteHeader')}
      className="sticky top-0 z-50 border-b border-[color:var(--bd-line)] bg-[rgb(255_255_255/0.86)] backdrop-blur-[16px]"
    >
      <div className="mx-auto flex h-[var(--bd-nav-h)] w-full max-w-[var(--bd-maxw)] items-center gap-2 px-4 sm:gap-4 sm:px-8">
        {/* `fit`: the mark's authored viewBox is less than half ink, so an
            unfitted `size={30}` draws a ~15px glyph sitting 2.4% above its own
            box centre — small, and optically high beside the wordmark no matter
            how the row is centred. See `MARK_VIEWBOX_TIGHT`. */}
        <Link
          to="/"
          aria-label={t('header.brandHome')}
          className="flex shrink-0 items-center gap-2.5 rounded-md text-[1.18rem] font-semibold leading-none tracking-[-0.03em] text-[color:var(--bd-ink)] transition hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--bd-blue)] focus-visible:ring-offset-2"
        >
          <Logo size={30} fit />
          <span className="hidden sm:inline">syncanix</span>
        </Link>

        {/* The nav takes the middle and the actions sit at the inline end. The
            spacer is on the LIST rather than a wrapper so the links stay
            centred in the row at wide widths and collapse to nothing below
            `lg`, where the menu button is the whole nav. */}
        <nav
          aria-label={t('header.primaryNav')}
          className="hidden min-w-0 flex-1 justify-center lg:flex"
        >
          <ul className="flex items-center gap-1">
            {SECTION_LINKS.map((link) => {
              const active = isNavLinkActive(pathname, link.href);
              return (
                <li key={link.href} className="shrink-0">
                  <Link
                    to={link.href}
                    {...(active ? { 'aria-current': 'page' as const } : {})}
                    className={`inline-flex items-center rounded-lg px-3 py-2 text-[0.94rem] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--bd-blue)] focus-visible:ring-offset-2 ${
                      active
                        ? 'bg-[color:var(--bd-sunk)] font-medium text-[color:var(--bd-ink)]'
                        : 'text-[color:var(--bd-muted)] hover:bg-[color:var(--bd-sunk)] hover:text-[color:var(--bd-ink)]'
                    }`}
                  >
                    {t(link.labelKey)}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="ms-auto flex shrink-0 items-center gap-2 sm:gap-3 lg:ms-0">
          {/* Kept in the bar at EVERY width. It already degrades to an
              icon-only trigger below `sm` (~31px), so it costs the row almost
              nothing — and a language control an Arabic or Hebrew reader has to
              open a menu to find is the one demotion that would actually cost
              somebody something. */}
          <LanguageSwitcher />
          <div className="hidden lg:block">
            <CtaLink href={appLoginUrl()} variant="ghost" size="sm">
              {t(CHROME_CTAS.secondary.labelKey.site)}
            </CtaLink>
          </div>
          {/* Sales-led acquisition: the primary CTA routes to the /contact form
              (locale-aware in-SPA Link), never to a self-serve sign-up — no
              payment processor is connected. */}
          <Link
            to={CHROME_ROUTES.contact}
            className={ctaClasses({ size: 'sm', className: 'whitespace-nowrap' })}
            onClick={() => {
              track(ANALYTICS_EVENTS.MARKETING_CONTACT_CTA_CLICKED, { location: 'header' });
            }}
          >
            {t(CHROME_CTAS.primary.labelKey.site)}
          </Link>
          <NavMenu links={SECTION_LINKS} />
        </div>
      </div>
    </header>
  );
}
